import { Typography, Button, LazyImage } from '@frontend/design_system'
import { Block } from '../Block'
import {
  DELETE_MODAL_DESCRIPTION,
  DELETE_MODAL_TITLE,
  FORCE_DELETE_MODAL_DESCRIPTION,
  FORCE_DELETE_MODAL_TITLE,
  DELETED_SUCCESS,
  HISTORY_LIST_ACTION,
  HISTORY_LIST_TITLE,
} from 'texts/termsAndConditions'
import styles from './styles.module.scss'
import trash from 'assets/icons/red600/red600_trash_20x20.webp'
import { useApiRequest } from 'hooks/useApiRequest'
import { termsApi } from 'api'
import { TERM_CONDITIONS_TYPES } from 'constants/termsAndConditions'
import { useState } from 'react'
import { ITermsAndConditionsItem } from 'api/terms/types'
import { useDispatch, useSelector } from 'react-redux'
import { setNotificationMessage } from 'redux/notifications/slice'
import { Confirmation } from 'ui/molecules/Confirmation'
import { scrollTop } from 'utils/scrollTop'
import { isMigratedUserSelector } from 'redux/login/selectors'
import { useCompanyInfo } from 'ui/hooks/useCompanyInfo'
import { useCompanyId } from 'hooks/useCompanyId'

interface IHistoryListProps {
  termConditionTypesId: TERM_CONDITIONS_TYPES
  setEditData: (data?: ITermsAndConditionsItem) => void
  editDataId?: number
  historyList: ITermsAndConditionsItem[]
  getList: VoidFunction
  reload: VoidFunction
}

interface IConfirmationData {
  label: string
  id: number
}

export const HistoryList = ({
  setEditData,
  editDataId,
  historyList,
  getList,
  reload,
}: IHistoryListProps) => {
  const dispatch = useDispatch()
  const [confirmationData, setConfirmationData] =
    useState<IConfirmationData | null>(null)
  const [forceDeleteModal, setForceDeleteModal] = useState<boolean>(false)
  const isMigratedUser = useSelector(isMigratedUserSelector)

  const deleteTermConditionRequest = useApiRequest(
    (id: number, forceDelete: boolean) =>
      termsApi.deleteTermConditionRequest(id, forceDelete),
    undefined,
    true
  )
  const companyId = useCompanyId()

  const { getCompanyInfo } = useCompanyInfo(false)

  const toggleConfirmationModal = (data: IConfirmationData | null) =>
    setConfirmationData(data)

  const closeForceDeleteModal = () => {
    setForceDeleteModal(false)
    toggleConfirmationModal(null)
  }

  const onDelete = async (id: number, forceDelete: boolean) => {
    const response = await deleteTermConditionRequest.apiRequest(
      id,
      forceDelete
    )

    if (response?.data) {
      if (!response.data.isDeleted) {
        setForceDeleteModal(true)
        return
      }
    }

    if (id === editDataId) {
      setEditData()
    }

    if (isMigratedUser) {
      getCompanyInfo(Number(companyId))
    }

    getList()
    dispatch(
      setNotificationMessage({
        notificationMessage: DELETED_SUCCESS,
      })
    )
    toggleConfirmationModal(null)
    setForceDeleteModal(false)
    reload()
  }

  const onEdit = (data: ITermsAndConditionsItem) => {
    setEditData(data)
    scrollTop()
  }

  if (!historyList?.length) {
    return null
  }

  return (
    <Block title={HISTORY_LIST_TITLE}>
      {historyList.map((item) => {
        const cors = item.countriesOfResidence.join(', ')

        return (
          <div key={cors} className={styles.row}>
            <Typography name="body1WMedium" Tag="div" className={styles.cors}>
              {cors}
            </Typography>
            <div className={styles.actions}>
              <Button
                label={HISTORY_LIST_ACTION}
                typographyName="body1WMedium"
                variant="link"
                onClick={() => onEdit(item)}
              />
              <LazyImage
                src={trash}
                width={20}
                height={20}
                onClick={() =>
                  toggleConfirmationModal({ label: cors, id: item.id })
                }
              />
            </div>
          </div>
        )
      })}
      {confirmationData && !forceDeleteModal && (
        <Confirmation
          confirmModalProps={{
            title: DELETE_MODAL_TITLE,
            onConfirm: () => onDelete(confirmationData.id, false),
            onClose: () => toggleConfirmationModal(null),
            question: DELETE_MODAL_DESCRIPTION,
          }}
        />
      )}
      {confirmationData && forceDeleteModal && (
        <Confirmation
          confirmModalProps={{
            title: FORCE_DELETE_MODAL_TITLE,
            onConfirm: () => onDelete(confirmationData.id, true),
            onClose: closeForceDeleteModal,
            question: FORCE_DELETE_MODAL_DESCRIPTION,
          }}
        />
      )}
    </Block>
  )
}
